import * as XLSX from "xlsx";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import {
  ByProductStockSummary,
  SubCategoryStockSummary,
} from "../../models/oilProduction-model";

// Utility function to parse numbers
const parseNumber = (val: any) => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const n = parseFloat(val.replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

// Format currency values
const rupee = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

// Prepare by-product stock data for export
const prepareByProductStockData = (stocks: ByProductStockSummary[]) => {
  if (!stocks || stocks.length === 0) return [];

  return stocks.map((item) => {
    const totalProduced = parseNumber(item.totalProducedLitres);
    const pendingStock = parseNumber(item.pendingLitres);
    const usedInBatch = parseNumber(item.usedInBatchLitres);
    const totalAvailable = parseNumber(item.totalAvailableStock);
    const allocatedStock = parseNumber(item.allocatedStock);
    const unit = item.unit || "L";

    // Prepare allocated products breakdown
    let allocatedDetails = `0 ${unit}`;
    if (item.allocatedProducts && item.allocatedProducts.length > 0) {
      allocatedDetails = item.allocatedProducts
        .map((p) => `${p.productName}: ${p.allocatedVolume} ${unit}`)
        .join(" | ");
    }

    return {
      "By-Product Name": item.byProductName || "0",
      "Total Produced": `${totalProduced.toFixed(2)} ${unit}`,
      "Available Stock (Pending)": `${pendingStock.toFixed(2)} ${unit}`,
      "Used in Batch": `${usedInBatch.toFixed(2)} ${unit}`,
      "Product Batch Available": `${totalAvailable.toFixed(2)} ${unit}`,
      "Allocated Stock": `${allocatedStock.toFixed(2)} ${unit}`,
      "Allocation Breakdown": allocatedDetails,
    };
  });
};

// Prepare sub-category stock data for export
const prepareSubCategoryStockData = (stocks: SubCategoryStockSummary[]) => {
  if (!stocks || stocks.length === 0) return [];

  return stocks.map((item) => {
    const totalStock = parseNumber(item.totalAvailableStock);

    return {
      "Sub-Category Name": item.subCategoryName || "0",
      "Total Available Stock": `${totalStock.toFixed(2)} L`,
    };
  });
};

// Main export function for production stock
export const productionStockExport = async (
  byProductStocks: ByProductStockSummary[],
  subCategoryStocks: SubCategoryStockSummary[],
  posIds: number[]
) => {
  try {
    if (!byProductStocks || !subCategoryStocks) {
      console.warn("No data available for export.");
      return;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Prepare data for by-product stocks
    const byProductData = prepareByProductStockData(byProductStocks);
    if (byProductData.length > 0) {
      const byProductWs = XLSX.utils.json_to_sheet(byProductData);

      // Set column widths for by-product sheet
      const byProductColumnWidths = [
        { wch: 25 }, // By-Product Name
        { wch: 18 }, // Total Produced
        { wch: 22 }, // Available Stock (Pending)
        { wch: 15 }, // Used in Batch
        { wch: 25 }, // Product Batch Available
        { wch: 18 }, // Allocated Stock
        { wch: 40 }, // Allocation Breakdown
      ];
      byProductWs["!cols"] = byProductColumnWidths;

      XLSX.utils.book_append_sheet(wb, byProductWs, "By-Product Stock");
    }

    // Prepare data for sub-category stocks
    const subCategoryData = prepareSubCategoryStockData(subCategoryStocks);
    if (subCategoryData.length > 0) {
      const subCategoryWs = XLSX.utils.json_to_sheet(subCategoryData);

      // Set column widths for sub-category sheet
      const subCategoryColumnWidths = [
        { wch: 25 }, // Sub-Category Name
        { wch: 22 }, // Total Available Stock
      ];
      subCategoryWs["!cols"] = subCategoryColumnWidths;

      XLSX.utils.book_append_sheet(wb, subCategoryWs, "Sub-Category Stock");
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `ProductionStockExport_${timestamp}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    console.log(
      `✅ Exported ${byProductData.length} by-products and ${subCategoryData.length} sub-categories successfully.`
    );
  } catch (error) {
    console.error("❌ Error exporting production stock data:", error);
    throw error;
  }
};

